import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

import { borderClassesError } from '../form/variants'
import { inputVariantListBase } from '../input/variants'
import { buttonIconVariantList } from '../button/variants'

export const selectClasses = {
  menuButton: [
    'relative',
    'flex',
    'flex-col',
    'w-full',
    'cursor-default',
    'select-none',
    'justify-center',
    'rounded-sm',
    'py-1.5',
    'pl-2',
    'pr-8',
    'text-sm',
    'outline-none',
    'focus:bg-gray-100',
    'focus:text-gray-900',
    'data-[disabled]:pointer-events-none',
    'data-[disabled]:opacity-50',
    'dark:focus:bg-gray-800',
    'dark:focus:text-gray-50',
  ],
  menuButtonIconContainer: [
    'absolute',
    'right-2',
    'flex',
    'items-center',
    'justify-center',
  ],
  menuButtonIcon: [
    'size-4',
  ],
  menuLabel: [
    'py-1.5',
    'pl-2',
    'pb-2',
    'pr-8',
    'text-xs',
    'text-neutral-400',
  ],
  group: [
    'p-1.5',
  ],
}

export const selectTriggerVariantList = {
  size: {
    '2xs': [
      inputVariantListBase.size['2xs'],
      'gap-[4px]',
    ],
    xs: [
      inputVariantListBase.size.xs,
      'gap-[5px]',
    ],
    sm: [
      inputVariantListBase.size.sm,
      'gap-[6px]',
    ],
    md: [
      inputVariantListBase.size.md,
      'gap-[7px]',
    ],
    lg: [
      inputVariantListBase.size.lg,
      'gap-[8px]',
    ],
    xl: 'h-12 text-base px-[18px] gap-[9px]',
    '2xl': 'h-14 text-lg/[26px] px-[20px] gap-[10px]',
  },
  hasError: {
    true: borderClassesError,
  },
}

export const selectTriggerBaseClasses = [
  'flex',
  'w-full',
  'items-center',
  'justify-between',
  'rounded-md',
  'border',
  'py-2',
  'border-border',
  'bg-background',
  'hover:bg-background',
  'text-neutral-800',
  'font-normal',
  'ring-offset-background',
  'focus:outline-none',
  'focus:ring-2',
  'focus:ring-ring',
  'focus:ring-offset-2',
  'disabled:cursor-not-allowed',
  'disabled:opacity-50',
  'data-[state=open]:border-neutral-800',
  'data-[placeholder]:text-neutral-400',
  'data-[state=open]:ring-1',
  'data-[state=open]:ring-neutral-800',
  '[&>span]:truncate',
]

export const selectTriggerVariants = cva(
  selectTriggerBaseClasses,
  {
    variants: selectTriggerVariantList,
  },
)
export type SelectTriggerVariants = VariantProps<typeof selectTriggerVariants>

export const selectTriggerIconVariants = cva(
  'text-neutral-800',
  {
    variants: buttonIconVariantList,
  },
)
export type SelectTriggerIconVariants = VariantProps<typeof selectTriggerIconVariants>

export const selectContentVariants = cva(
  [
    'relative',
    'z-50',
    'max-h-96',
    'min-w-32',

    'overflow-hidden',
    'rounded-xl',
    'border',
    'border-neutral-300',
    'bg-white',
    'text-neutral-800',
    'shadow-lg',
    'data-[state=open]:animate-in',
    'data-[state=closed]:animate-out',
    'data-[state=closed]:fade-out-0',
    'data-[state=open]:fade-in-0',
    'data-[state=closed]:zoom-out-95',
    'data-[state=open]:zoom-in-95',
    'data-[side=bottom]:slide-in-from-top-2',
    'data-[side=left]:slide-in-from-right-2',
    'data-[side=right]:slide-in-from-left-2',
    'data-[side=top]:slide-in-from-bottom-2',
    'dark:border-gray-800',
    'dark:bg-gray-950',
    'dark:text-gray-50',
  ],
  {
    variants: {
      position: {
        popper: [
          'data-[side=bottom]:translate-y-1',
          'data-[side=left]:-translate-x-1',
          'data-[side=right]:translate-x-1',
          'data-[side=top]:-translate-y-1',
        ],
        'item-aligned': '',
      },
    },
  },
)
export type SelectContentVariants = VariantProps<typeof selectContentVariants>
